import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/data";
import { scrollToSection } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const HEADLINE = ["Your personal", "Family CFO", "for generational", "prosperity."];

const Line = ({ children, delay }) => (
  <span className="masked-line" style={{ paddingBottom: "0.12em" }}>
    <motion.span
      style={{ display: "block" }}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.8]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen w-full overflow-hidden bg-[#0F1E3D]" data-testid="hero-section">
      {/* Parallax architectural background */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Architectural structure"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-[#0F1E3D]" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-[#0F1E3D] via-[#0F1E3D]/30 to-[#0F1E3D]/60" />
      <div className="grain-overlay z-20" />

      {/* SEBI compliance badge is rendered inline within the content block below */}

      <motion.div
        style={{ y: textY }}
        className="relative z-30 max-w-[1400px] mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-32 pb-16 lg:pt-40 lg:pb-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4, ease: EASE }}
          className="mb-8 inline-flex w-fit items-center gap-2.5 rounded-full border border-[#C89933]/40 bg-white/5 backdrop-blur-sm px-4 py-2"
          data-testid="sebi-badge"
        >
          <ShieldCheck className="h-4 w-4 text-[#C89933]" strokeWidth={1.6} />
          <span className="overline text-[0.62rem] text-[#FAFAF8]/90">{BRAND.sebi}</span>
        </motion.div>

        <h1 className="font-serif-display text-[#FAFAF8] text-4xl leading-[1] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight font-medium max-w-4xl">
          <Line delay={0.55}>Your personal</Line>
          <Line delay={0.68}>
            <span className="italic text-[#C89933]">Family CFO</span>
          </Line>
          <Line delay={0.81}>for generational</Line>
          <Line delay={0.94}>prosperity.</Line>
        </h1>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: EASE }}
            className="max-w-md text-[#FAFAF8]/70 text-base lg:text-lg leading-relaxed font-light"
          >
            We do the heavy lifting of wealth stewardship, so your hard-earned money works optimally for your family at every phase of life.
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: EASE }}
            onClick={() => scrollToSection("contact")}
            className="group inline-flex items-center gap-3 self-start rounded-full bg-[#C89933] px-8 py-4 text-[#0F1E3D] font-medium hover:bg-[#FAFAF8] transition-colors duration-400"
            data-testid="hero-cta"
          >
            Book a Consultation
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.8} />
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
